import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { useAccount, useSignTypedData } from "wagmi";
import { RecoveryMessageInfo } from "@/core/recovery";
import { getGearboxApiUrl } from "@/utils/api-fetch";
import { useCrossChainMultisig } from "./use-cross-chain-multisig";
import { useWeb3 } from "./use-web3";

export function useSignRecovery({
  recovery,
  updateRecoveries,
}: {
  recovery: RecoveryMessageInfo;
  updateRecoveries: (recovery: RecoveryMessageInfo) => void;
}) {
  const { address } = useAccount();
  const { signers, threshold } = useCrossChainMultisig();
  const { crossChainMultisig } = useWeb3();
  const { signTypedDataAsync } = useSignTypedData();

  const canSign = useMemo(
    () =>
      address !== undefined &&
      (signers || []).some(s => s.toLowerCase() === address.toLowerCase()) &&
      !recovery.signatures.some(
        s => s.signer.toLowerCase() === address.toLowerCase(),
      ) &&
      recovery.transactionHash === undefined,
    [address, signers, recovery],
  );

  const mutation = useMutation({
    mutationFn: async () => {
      if (!address) return;
      if (!crossChainMultisig) return;

      const signRecoveryTypeData = crossChainMultisig.computeRecoveryDigest(
        recovery.chainId,
        recovery.startingBatchHash,
      );

      const signature = await signTypedDataAsync(signRecoveryTypeData);

      const baseUrl = getGearboxApiUrl();

      const response = await fetch(`${baseUrl}/ccm/recovery`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chainId: recovery.chainId,
          startingBatchHash: recovery.startingBatchHash,
          signature,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error("Failed to validate signature");
        throw new Error(error.error || "Failed to validate signature");
      }

      updateRecoveries({
        ...recovery,
        signatures: [
          ...recovery.signatures,
          {
            signature,
            signer: address,
          },
        ],
      });
    },
    onSuccess: () => {
      toast.success("Recovery message signed");
    },
    onError: error => {
      toast.error("Error validating signature", {
        description: error.message,
      });
    },
  });

  return {
    canSign,
    threshold: threshold || 0,
    sign: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error as Error | null,
  };
}
